/**
 * Pure layout math for the truck console. All values are CSS pixels relative
 * to the console root (top-left origin). Feed rects are integer-aligned so the
 * WebGL renderer can use them directly as scissor/viewport rects (after
 * multiplying by devicePixelRatio and flipping y).
 */

export interface Rect { x: number; y: number; w: number; h: number }

export interface ConsoleGeometry {
  /** Base unit (root font-size) in px. Everything in CSS scales with it via em. */
  u: number;
  bezel: number;
  umdBig: number;
  umdTile: number;
  top: Rect;
  hints: Rect;
  side: Rect;
  deck: Rect;
  program: Rect;
  preview: Rect;
  wall: Rect[];
  /** Compact mode for small windows (< ~1180px wide or < 700px tall). */
  compact: boolean;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const r = Math.round;

export function computeConsoleLayout(W: number, H: number, tileCount: number): ConsoleGeometry {
  const compact = W < 1180 || H < 700;
  const u = clamp(Math.min(W / 1920, H / 1080) * 15, 10.5, 20);
  const pad = r(u * 0.8);
  const gap = r(u * 0.75);
  const bezel = Math.max(3, r(u * 0.32));
  const umdBig = r(u * 1.9);
  const umdTile = r(u * 1.65);
  const topH = r(u * 3.3);
  const hintsH = r(u * 2.1);
  const sideW = r(clamp(W * 0.205, compact ? 236 : 270, 460));

  const top: Rect = { x: 0, y: 0, w: W, h: topH };
  const hints: Rect = { x: 0, y: H - hintsH, w: W, h: hintsH };
  const mx = pad, my = topH + pad;
  const mw = W - sideW - pad * 3;
  const mh = H - topH - hintsH - pad * 2;
  const side: Rect = { x: W - pad - sideW, y: my, w: sideW, h: mh };

  // --- top row: PGM (big) + PVW column (PVW monitor + deck under it)
  const PVW_RATIO = 0.6;
  const byWidth = (mw - 4 * bezel - gap) / (1 + PVW_RATIO);
  const byHeight = (mh * 0.6 - bezel - umdBig) * (16 / 9);
  const pgmW = Math.floor(Math.min(byWidth, byHeight));
  const pgmH = Math.floor((pgmW * 9) / 16);
  const program: Rect = { x: mx + bezel, y: my + bezel, w: pgmW, h: pgmH };
  const topRowBottom = my + bezel + pgmH + umdBig;

  const colX = mx + pgmW + 2 * bezel + gap;
  const colW = mx + mw - colX;
  const deckMin = r(u * (compact ? 8.6 : 9.6));
  let pvwW = Math.min(colW - 2 * bezel, Math.floor(pgmW * 0.78));
  let pvwH = Math.floor((pvwW * 9) / 16);
  const maxPvwH = topRowBottom - my - deckMin - gap - bezel - umdBig;
  if (pvwH > maxPvwH) { pvwH = Math.max(40, Math.floor(maxPvwH)); pvwW = Math.floor((pvwH * 16) / 9); }
  const preview: Rect = { x: colX + bezel, y: my + bezel, w: pvwW, h: pvwH };
  const deckY = preview.y + pvwH + umdBig + gap;
  const deck: Rect = { x: colX, y: deckY, w: colW, h: Math.max(deckMin, topRowBottom - deckY) };

  // --- monitor wall: choose the column count that maximises tile size
  const wallY = Math.max(topRowBottom, deck.y + deck.h) + gap;
  const wallH = my + mh - wallY;
  const gapT = Math.max(4, r(gap * 0.7));
  let best = { cols: 1, w: 0 };
  const n = Math.max(1, tileCount);
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const wW = (mw - (cols - 1) * gapT) / cols - 2 * bezel;
    const wH = ((wallH - (rows - 1) * gapT) / rows - bezel - umdTile) * (16 / 9);
    const w = Math.floor(Math.min(wW, wH));
    if (w > best.w) best = { cols, w };
  }
  const tw = Math.max(16, best.w), th = Math.floor((tw * 9) / 16);
  const cols = best.cols, rows = Math.ceil(n / cols);
  const cellW = tw + 2 * bezel, cellH = th + bezel + umdTile;
  const gridH = rows * cellH + (rows - 1) * gapT;
  const y0 = wallY + Math.max(0, (wallH - gridH) / 2);
  const wall: Rect[] = [];
  for (let i = 0; i < tileCount; i++) {
    const row = Math.floor(i / cols), col = i % cols;
    const inRow = row === rows - 1 ? n - row * cols : cols;
    const rowW = inRow * cellW + (inRow - 1) * gapT;
    const x0 = mx + (mw - rowW) / 2;
    wall.push({ x: r(x0 + col * (cellW + gapT) + bezel), y: r(y0 + row * (cellH + gapT) + bezel), w: tw, h: th });
  }

  return { u, bezel, umdBig, umdTile, top, hints, side, deck, program, preview, wall, compact };
}

/** Outer bezel rect around a feed rect (feed + bezel + under-monitor display). */
export function bezelRect(feed: Rect, bezel: number, umd: number): Rect {
  return { x: feed.x - bezel, y: feed.y - bezel, w: feed.w + 2 * bezel, h: feed.h + bezel + umd };
}

/** SVG path (evenodd) for a full-size plate with holes punched for every feed. */
export function holesPath(W: number, H: number, holes: readonly Rect[]): string {
  let d = `M0 0H${W}V${H}H0Z`;
  for (const h of holes) d += `M${h.x} ${h.y}h${h.w}v${h.h}h${-h.w}Z`;
  return d;
}
